import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, SignOut, ArrowsClockwise, DownloadSimple, ShieldCheck, Tag, ChatCircle, MagnifyingGlass, X as XIcon } from "@phosphor-icons/react";
import Logo from "@/components/Logo";
import { adminLogout, fetchDownloads, isAdminAuthenticated, fetchDiscount, updateDiscount } from "@/lib/admin";
import AdminMessagesTab from "@/components/admin/AdminMessagesTab";
import Pagination, { paginate, DEFAULT_PAGE_SIZE } from "@/components/admin/Pagination";
import SortableTH from "@/components/admin/SortableTH";
import Highlight from "@/components/admin/Highlight";
import { searchItems, sortItems } from "@/lib/adminTableUtils";

function toISO(d) { return d ? new Date(d + "T00:00:00Z").toISOString() : ""; }
function toISOEnd(d) { return d ? new Date(d + "T23:59:59Z").toISOString() : ""; }
function fmt(dt) {
  if (!dt) return "-";
  const d = new Date(dt);
  return d.toLocaleString(undefined, { year: "numeric", month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" });
}

export default function AdminDashboard() {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({ total: 0, free: 0, paid: 0, revenue_usd: 0, revenue_inr: 0 });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [kind, setKind] = useState(""); // "" | "free" | "paid"
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [discount, setDiscount] = useState({ enabled: false, percent: 0, label: "", starts_at: "", ends_at: "", source: "none", active: false });
  const [discountSaving, setDiscountSaving] = useState(false);
  const [discountMsg, setDiscountMsg] = useState("");
  // Which top-level tab is visible. "downloads" (existing analytics view) or
  // "messages" (contact-form inbox).
  const [tab, setTab] = useState("downloads");
  // Pagination for the Downloads table — client-side slice of `rows`.
  // Reset to page 1 whenever filters are applied or the data reloads.
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  // Live search + column sort for the Downloads table. Search matches any
  // of session_id / order_id / payment_id / template / kind; sort cycles
  // asc → desc → off on each header click.
  const [searchQuery, setSearchQuery] = useState("");
  const [sortConfig, setSortConfig] = useState({ field: "created_at", direction: "desc" });
  const processedRows = useMemo(() => {
    const filtered = searchItems(rows, searchQuery, [
      "session_id", "order_id", "payment_id", "template", "kind",
    ]);
    return sortItems(filtered, sortConfig);
  }, [rows, searchQuery, sortConfig]);
  const visibleRows = useMemo(() => paginate(processedRows, page, pageSize), [processedRows, page, pageSize]);
  // Reset to page 1 whenever the search query changes so users don't land
  // on an empty page after narrowing.
  useEffect(() => { setPage(1); }, [searchQuery]);

  useEffect(() => {
    // Initial load uses empty filters — filters are applied via the Apply
    // button which calls load() explicitly. Inlined here so the effect can
    // safely depend only on the stable `nav` reference. The auth probe
    // (`isAdminAuthenticated`) also verifies the httpOnly cookie is valid;
    // fetchDownloads itself will 401 anyway if the cookie is missing.
    (async () => {
      const authed = await isAdminAuthenticated();
      if (!authed) { nav("/admin"); return; }
      setLoading(true);
      setErr("");
      try {
        const data = await fetchDownloads({});
        setRows(data.downloads || []);
        setStats(data.stats || {});
        setPage(1);
      } catch (e) {
        if (e?.response?.status === 401) {
          await adminLogout();
          nav("/admin");
          return;
        }
        setErr(e?.response?.data?.detail || e.message || "Failed to load");
      } finally {
        setLoading(false);
      }
    })();
    // Load current discount config in parallel.
    (async () => {
      try {
        const d = await fetchDiscount();
        setDiscount({
          enabled: !!d.enabled,
          percent: d.percent || 0,
          label: d.label || "",
          starts_at: d.starts_at || "",
          ends_at: d.ends_at || "",
          source: d.source || "none",
          active: !!d.active,
        });
      } catch (e) {
        /* silent — non-critical for the analytics view */
      }
    })();
  }, [nav]);

  const saveDiscount = async () => {
    setDiscountSaving(true);
    setDiscountMsg("");
    try {
      const payload = {
        enabled: !!discount.enabled,
        percent: Number(discount.percent) || 0,
        label: discount.label || "",
        starts_at: discount.starts_at || null,
        ends_at: discount.ends_at || null,
      };
      const d = await updateDiscount(payload);
      setDiscount({
        enabled: !!d.enabled,
        percent: d.percent || 0,
        label: d.label || "",
        starts_at: d.starts_at || "",
        ends_at: d.ends_at || "",
        source: d.source || "none",
        active: !!d.active,
      });
      setDiscountMsg(payload.percent === 0 ? "Admin discount cleared." : "Discount saved.");
    } catch (e) {
      setDiscountMsg(e?.response?.data?.detail || "Failed to save discount");
    } finally {
      setDiscountSaving(false);
      setTimeout(() => setDiscountMsg(""), 4000);
    }
  };

  const load = async () => {
    setLoading(true);
    setErr("");
    try {
      const params = {};
      if (fromDate) params.from = toISO(fromDate);
      if (toDate) params.to = toISOEnd(toDate);
      if (kind) params.kind = kind;
      const data = await fetchDownloads(params);
      setRows(data.downloads || []);
      setStats(data.stats || {});
      setPage(1);
    } catch (e) {
      if (e?.response?.status === 401) {
        await adminLogout();
        nav("/admin");
        return;
      }
      setErr(e?.response?.data?.detail || e.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => { await adminLogout(); nav("/admin"); };

  const csv = useMemo(() => {
    const header = ["created_at", "kind", "template", "page_size", "font_scale", "density", "session_id", "session_total_downloads", "order_id", "payment_id"];
    const lines = [header.join(",")];
    for (const r of rows) {
      lines.push(header.map((h) => JSON.stringify(r[h] ?? "")).join(","));
    }
    return lines.join("\n");
  }, [rows]);

  const downloadCsv = () => {
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `downloads_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-white text-neutral-950 font-sans" data-testid="admin-dashboard-page">
      {/* Header */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/85 border-b border-black/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-14 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-2 text-sm text-neutral-700 hover:text-black" data-testid="admin-back-home">
              <ArrowLeft size={16} weight="bold" />
              <span className="hidden sm:inline">Home</span>
            </Link>
            <div className="h-4 w-px bg-black/20" />
            <Logo />
            <span className="label-mono ml-2">Admin</span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={load} disabled={loading} className="btn-secondary text-xs" data-testid="admin-refresh"><ArrowsClockwise size={13} weight="bold" />Refresh</button>
            <button onClick={downloadCsv} disabled={!rows.length} className="btn-secondary text-xs" data-testid="admin-download-csv"><DownloadSimple size={13} weight="bold" />CSV</button>
            <button onClick={signOut} className="btn-secondary text-xs" data-testid="admin-signout"><SignOut size={13} weight="bold" />Sign out</button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 lg:px-10 py-8 lg:py-12">
        {/* Top-level tabs — Downloads (analytics) vs Messages (contact-form
            inbox). Keeps the admin surface compact without cramming both
            views onto one screen. */}
        <div className="flex items-center gap-1 border-b border-black/10 mb-8" data-testid="admin-tabs">
          {[
            { id: "downloads", label: "Downloads", icon: DownloadSimple },
            { id: "messages", label: "Messages", icon: ChatCircle },
          ].map((t) => {
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => setTab(t.id)}
                className={`inline-flex items-center gap-2 px-4 py-2.5 text-xs font-mono uppercase tracking-widest -mb-px border-b-2 transition-colors ${
                  active
                    ? "border-neutral-950 text-neutral-950"
                    : "border-transparent text-neutral-500 hover:text-neutral-900"
                }`}
                data-testid={`admin-tab-${t.id}`}
                aria-selected={active}
              >
                <t.icon size={14} weight="bold" />
                {t.label}
              </button>
            );
          })}
        </div>

        {tab === "messages" ? (
          <AdminMessagesTab />
        ) : (
        <>
        <div className="label-mono mb-4">
          <span className="inline-block w-6 h-px bg-black/40 align-middle mr-2" />
          Downloads · Analytics
        </div>
        <h1 className="font-heading font-black tracking-tighter text-4xl lg:text-5xl leading-[1.02]">
          Downloads
        </h1>

        {/* Stats cards */}
        <div className="mt-8 grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="admin-stats">
          <StatCard label="Total downloads" value={stats.total ?? 0} testid="stat-total" />
          <StatCard label="Free" value={stats.free ?? 0} testid="stat-free" />
          <StatCard label="Paid" value={stats.paid ?? 0} testid="stat-paid" accent />
          <StatCard label="Unique sessions" value={stats.unique_sessions ?? 0} testid="stat-sessions" />
          <StatCard label="Revenue" value={`$${stats.revenue_usd ?? 0} · ₹${stats.revenue_inr ?? 0}`} testid="stat-revenue" accent />
        </div>

        {/* Discount management */}
        <div className="mt-8 border border-black/10 p-4" data-testid="admin-discount-card">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Tag size={14} weight="fill" className="text-[#FF2A00]" />
              <span className="font-heading font-semibold text-lg tracking-tight">Discount</span>
              <span
                className={`ml-2 text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 border ${discount.active ? "border-[#FF2A00] text-[#FF2A00]" : "border-neutral-300 text-neutral-400"}`}
                data-testid="admin-discount-status"
              >
                {discount.active ? "Active" : "Inactive"}
              </span>
              <span className="text-[10px] font-mono uppercase tracking-widest text-neutral-400">
                Source: {discount.source}
              </span>
            </div>
            {discountMsg && (
              <span className="text-xs text-neutral-500" data-testid="admin-discount-msg">{discountMsg}</span>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
            <div>
              <div className="label-mono mb-1.5">Enabled</div>
              <label className="inline-flex items-center gap-2 py-2">
                <input
                  type="checkbox"
                  checked={!!discount.enabled}
                  onChange={(e) => setDiscount((d) => ({ ...d, enabled: e.target.checked }))}
                  data-testid="admin-discount-enabled"
                />
                <span className="text-xs text-neutral-600">Turn on / off</span>
              </label>
            </div>
            <div>
              <div className="label-mono mb-1.5">Percent (0–100)</div>
              <input
                type="number"
                min={0}
                max={100}
                value={discount.percent}
                onChange={(e) => setDiscount((d) => ({ ...d, percent: e.target.value }))}
                className="field-input w-full"
                data-testid="admin-discount-percent"
              />
            </div>
            <div>
              <div className="label-mono mb-1.5">Label</div>
              <input
                type="text"
                placeholder="e.g. Launch offer"
                value={discount.label}
                onChange={(e) => setDiscount((d) => ({ ...d, label: e.target.value }))}
                className="field-input w-full"
                data-testid="admin-discount-label"
                maxLength={60}
              />
            </div>
            <div>
              <div className="label-mono mb-1.5">Starts at (optional)</div>
              <input
                type="datetime-local"
                value={discount.starts_at ? discount.starts_at.slice(0, 16) : ""}
                onChange={(e) => setDiscount((d) => ({ ...d, starts_at: e.target.value ? new Date(e.target.value).toISOString() : "" }))}
                className="field-input w-full"
                data-testid="admin-discount-starts"
              />
            </div>
            <div>
              <div className="label-mono mb-1.5">Ends at (optional)</div>
              <input
                type="datetime-local"
                value={discount.ends_at ? discount.ends_at.slice(0, 16) : ""}
                onChange={(e) => setDiscount((d) => ({ ...d, ends_at: e.target.value ? new Date(e.target.value).toISOString() : "" }))}
                className="field-input w-full"
                data-testid="admin-discount-ends"
              />
            </div>
          </div>
          <div className="mt-3 flex items-center justify-between">
            <div className="text-[11px] text-neutral-500 leading-snug">
              Setting percent to <b>0</b> clears the admin override. If <code>DISCOUNT_PERCENT</code> is set in deployment env vars, that value applies as fallback.
            </div>
            <button
              onClick={saveDiscount}
              disabled={discountSaving}
              className="btn-primary text-xs"
              data-testid="admin-discount-save"
            >
              {discountSaving ? "Saving…" : "Save discount"}
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="mt-8 border border-black/10 p-4 grid grid-cols-1 md:grid-cols-4 gap-4" data-testid="admin-filters">
          <div>
            <div className="label-mono mb-1.5">From</div>
            <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="field-input w-full" data-testid="admin-filter-from" />
          </div>
          <div>
            <div className="label-mono mb-1.5">To</div>
            <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="field-input w-full" data-testid="admin-filter-to" />
          </div>
          <div>
            <div className="label-mono mb-1.5">Kind</div>
            <select value={kind} onChange={(e) => setKind(e.target.value)} className="field-input w-full" data-testid="admin-filter-kind">
              <option value="">All</option>
              <option value="free">Free</option>
              <option value="paid">Paid</option>
            </select>
          </div>
          <div className="flex items-end gap-2">
            <button onClick={load} disabled={loading} className="btn-primary flex-1 justify-center" data-testid="admin-filter-apply">
              {loading ? "Loading…" : "Apply filters"}
            </button>
            <button
              onClick={() => { setFromDate(""); setToDate(""); setKind(""); setTimeout(load, 0); }}
              className="btn-secondary"
              data-testid="admin-filter-reset"
            >Reset</button>
          </div>
        </div>

        {err && <div className="mt-4 text-sm text-red-600 border border-red-200 bg-red-50 px-3 py-2">{err}</div>}

        {/* Search bar — client-side filter across session / order / payment / template */}
        <div className="mt-8 flex items-center gap-3 flex-wrap" data-testid="admin-search-wrapper">
          <div className="relative flex-1 min-w-[240px] max-w-md">
            <MagnifyingGlass size={14} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400 pointer-events-none" />
            <input
              type="search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search session, order, payment, template…"
              className="field-input w-full pl-9 pr-9"
              data-testid="admin-search-input"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 w-5 h-5 inline-flex items-center justify-center text-neutral-400 hover:text-neutral-900"
                data-testid="admin-search-clear"
                aria-label="Clear search"
              >
                <XIcon size={12} weight="bold" />
              </button>
            )}
          </div>
          <div className="text-[11px] font-mono uppercase tracking-widest text-neutral-500" data-testid="admin-search-count">
            {processedRows.length} / {rows.length}
          </div>
        </div>

        {/* Downloads table */}
        <div className="mt-4 border border-black/10 overflow-x-auto" data-testid="admin-table-wrapper">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 text-left text-neutral-500 font-mono uppercase tracking-widest text-[11px]">
              <tr>
                <SortableTH field="created_at" sortConfig={sortConfig} onSort={setSortConfig}>When</SortableTH>
                <SortableTH field="kind" sortConfig={sortConfig} onSort={setSortConfig}>Kind</SortableTH>
                <SortableTH field="template" sortConfig={sortConfig} onSort={setSortConfig}>Template</SortableTH>
                <SortableTH field="page_size" sortConfig={sortConfig} onSort={setSortConfig}>Page</SortableTH>
                <SortableTH field="font_scale" sortConfig={sortConfig} onSort={setSortConfig}>Font</SortableTH>
                <SortableTH field="density" sortConfig={sortConfig} onSort={setSortConfig}>Density</SortableTH>
                <SortableTH field="session_total_downloads" sortConfig={sortConfig} onSort={setSortConfig}>Session · #</SortableTH>
                <SortableTH field="order_id" sortConfig={sortConfig} onSort={setSortConfig}>Order ID</SortableTH>
                <SortableTH field="payment_id" sortConfig={sortConfig} onSort={setSortConfig}>Payment ID</SortableTH>
              </tr>
            </thead>
            <tbody data-testid="admin-table-body">
              {processedRows.length === 0 && !loading && (
                <tr><td colSpan={9} className="p-8 text-center text-neutral-500" data-testid="admin-table-empty">
                  {searchQuery ? `No matches for "${searchQuery}".` : "No downloads for the selected filter."}
                </td></tr>
              )}
              {visibleRows.map((r) => (
                <tr key={r.id} className="border-t border-black/5 hover:bg-neutral-50" data-testid={`admin-row-${r.id}`}>
                  <td className="p-3 whitespace-nowrap font-mono text-[12px]">{fmt(r.created_at)}</td>
                  <td className="p-3">
                    {r.kind === "paid" ? (
                      <span className="inline-flex items-center gap-1 text-[11px] font-mono uppercase tracking-widest text-[#2400FF]">
                        <ShieldCheck size={10} weight="fill" /> Paid
                      </span>
                    ) : (
                      <span className="text-[11px] font-mono uppercase tracking-widest text-neutral-500">Free</span>
                    )}
                  </td>
                  <td className="p-3">{r.template ? <Highlight text={r.template} query={searchQuery} /> : "-"}</td>
                  <td className="p-3">{r.page_size || "-"}</td>
                  <td className="p-3">{r.font_scale ? `${Math.round(r.font_scale * 10)}pt` : "-"}</td>
                  <td className="p-3">{r.density || "-"}</td>
                  <td className="p-3 whitespace-nowrap" data-testid={`admin-row-${r.id}-session`}>
                    {r.session_id ? (
                      <span title={r.session_id}>
                        <span className="font-mono text-[11px] text-neutral-500">
                          <Highlight text={r.session_id.slice(0, 8)} query={searchQuery} />…
                        </span>
                        <span className="ml-2 inline-block min-w-[22px] text-center px-1.5 py-0.5 border border-black/10 bg-neutral-50 font-mono text-[11px] text-neutral-900" data-testid={`admin-row-${r.id}-session-count`}>{r.session_total_downloads ?? "-"}</span>
                      </span>
                    ) : "-"}
                  </td>
                  <td className="p-3 font-mono text-[11px] text-neutral-500">
                    {r.order_id ? <Highlight text={r.order_id} query={searchQuery} /> : "-"}
                  </td>
                  <td className="p-3 font-mono text-[11px] text-neutral-500">
                    {r.payment_id ? <Highlight text={r.payment_id} query={searchQuery} /> : "-"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pagination
            totalItems={processedRows.length}
            page={page}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={(n) => { setPageSize(n); setPage(1); }}
            testidPrefix="admin-table-pagination"
          />
        </div>
        </>
        )}
      </main>
    </div>
  );
}

function StatCard({ label, value, accent, testid }) {
  return (
    <div className={`border ${accent ? "border-black bg-neutral-950 text-white" : "border-black/10"} p-4`} data-testid={testid}>
      <div className={`label-mono mb-1 ${accent ? "text-neutral-400" : ""}`}>{label}</div>
      <div className="font-heading font-black text-3xl tracking-tight">{value}</div>
    </div>
  );
}
